import { css, cx } from "@emotion/css";
import type { PrimaryPageAction, PageAction } from "@octopusdeploy/design-system-components";
import { Badge, Button, Collapse, tableStyles, Tooltip, LegacyDataTable, LegacyDataTableBody, LegacyDataTableHeader, LegacyDataTableHeaderColumn, LegacyDataTableRow, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import { ExperimentIcon, ChevronDownIcon, ChevronUpIcon } from "@octopusdeploy/design-system-icons";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { Repository, EnvironmentResource, DeploymentPermissionsReportResponse, UserWithPermissionToDeploy, DeploymentPermissionsReportEnvironmentResult, DeploymentPermissionsReportProjectResult } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useState, useEffect } from "react";
import { client, repository } from "~/clientInstance";
import { EnvironmentChip } from "~/components/Chips";
import { EnvironmentV2MultiSelect } from "~/components/MultiSelect/EnvironmentV2MultiSelect";
import InternalLink from "~/components/Navigation/InternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import Section from "~/components/Section";
import { createCreateReportEvent, createExportReportCSVEvent } from "./DeploymentPermissionsReportAnalytics";
interface LoaderData {
    environmentsSelectorListItems: EnvironmentSelectorListItem[];
}
interface EnvironmentSelectorListItem {
    Id: string;
    DisplayName: string;
    SpaceId: string;
    SpaceName: string;
}
interface DeploymentPermissionsReportPageProps {
    loaderData: LoaderData;
    queryParams: DeploymentPermissionsReportPageQueryParams;
    setQueryParams: (params: DeploymentPermissionsReportPageQueryParams) => void;
}
export interface DeploymentPermissionsReportPageQueryParams {
    environmentIds: string[];
}
export async function deploymentPermissionsReportPageLoader(repository: Repository): Promise<LoaderData> {
    const spaces = await repository.Spaces.all();
    const allEnvironments = await repository.Environments.getAllEnvironmentAcrossAllSpaces();
    const spaceNamesById = spaces.reduce((acc: Record<string, string>, space) => {
        acc[space.Id] = space.Name;
        return acc;
    }, {});
    const environmentsById = allEnvironments.reduce((acc: Record<string, EnvironmentResource>, env) => {
        acc[env.Id] = env;
        return acc;
    }, {});
    const environmentsSelectorListItems = allEnvironments
        .map((e: EnvironmentResource) => ({
        Id: e.Id,
        DisplayName: `${e.Name} (${spaceNamesById[e.SpaceId]})`,
        SpaceId: e.SpaceId,
        SpaceName: spaceNamesById[e.SpaceId],
        SortOrder: e.SortOrder,
    }))
        .sort((a, b) => {
        // Sort by Space first, then by the user-defined sort order
        if (a.SpaceName !== b.SpaceName) {
            return a.SpaceName.localeCompare(b.SpaceName);
        }
        return a.SortOrder - b.SortOrder;
    });
    return {
        environmentsSelectorListItems: environmentsSelectorListItems,
    };
}
export function DeploymentPermissionsReportPage({ loaderData: { environmentsSelectorListItems }, queryParams, setQueryParams }: DeploymentPermissionsReportPageProps) {
    const trackEvent = useTrackEvent();
    // Separate out the changing of the dropdown from the state used to trigger the report regeneration
    const [selectedEnvironmentIds, setSelectedEnvironmentIds] = useState<string[]>(queryParams.environmentIds || []);
    const [reportParams, setReportParams] = useState<string[] | undefined>(selectedEnvironmentIds.length > 0 ? selectedEnvironmentIds : undefined);
    // Update the dropdown when the querystring changes
    useEffect(() => {
        setSelectedEnvironmentIds(queryParams.environmentIds || []);
    }, [queryParams.environmentIds]);
    const { result: pagingData } = useQuery((repository): Promise<DeploymentPermissionsReportResponse> | Promise<undefined> => {
        if (!reportParams || reportParams.length === 0) {
            return Promise.resolve(undefined);
        }
        return repository.PlatformHub.getDeploymentPermissionsToEnvironmentReport(reportParams);
    }, [reportParams], "Get who can deploy to environment report", { initialResult: undefined });
    const enableCreateReport = selectedEnvironmentIds.length > 0 && selectedEnvironmentIds != reportParams;
    const handleCreateReport = async () => {
        if (selectedEnvironmentIds.length === 0)
            return;
        setReportParams(selectedEnvironmentIds);
        trackEvent(createCreateReportEvent({ selectedEnvironmentsCount: selectedEnvironmentIds.length }));
    };
    const enableCsvExport = pagingData?.Results && selectedEnvironmentIds == reportParams;
    const handleDownloadCsv = async () => {
        trackEvent(createExportReportCSVEvent({ selectedEnvironmentsCount: selectedEnvironmentIds.length }));
        repository.PlatformHub.downloadDeploymentPermissionsReportCsv(selectedEnvironmentIds);
    };
    const primaryAction: PrimaryPageAction = {
        type: "button",
        label: "Create Report",
        onClick: handleCreateReport,
        disabled: !enableCreateReport,
    };
    const pageActions: PageAction[] = enableCsvExport
        ? [
            {
                type: "button",
                label: "Download CSV",
                onClick: () => handleDownloadCsv(),
                buttonType: "secondary",
            },
        ]
        : [];
    const alphaBadge = (<div className={styles.badgeContainer}>
            <Tooltip content={"This feature is not finished or fully tested."}>
                <Badge variant={"feature"} size={"medium"} label={"Alpha"} icon={<ExperimentIcon size={20} color={themeTokens.color.badge.icon.feature}/>}/>
            </Tooltip>
        </div>);
    return (<PageContent header={{
            title: "Deployment Permissions",
            breadcrumbs: [{ label: "Reports", pageUrl: links.complianceReportsPage.generateUrl() }],
            showBreadcrumbBackIcon: true,
            primaryAction,
            pageActions,
            chip: alphaBadge,
        }}>
            <Section>
                <EnvironmentV2MultiSelect label="Choose Environments" value={selectedEnvironmentIds} items={environmentsSelectorListItems.map((i) => ({ Id: i.Id, Name: i.DisplayName, Type: "Static" as const }))} onChange={(newValue: string[]) => {
            setSelectedEnvironmentIds(newValue);
            setQueryParams({ environmentIds: newValue });
        }}/>
            </Section>
            {pagingData?.Results && pagingData.Results.map((environmentResults) => <ReportDataTable key={environmentResults.EnvironmentId} environmentResults={environmentResults}/>)}
        </PageContent>);
}
interface ReportDataTableProps {
    environmentResults: DeploymentPermissionsReportEnvironmentResult;
}
function ReportDataTable({ environmentResults: environment }: ReportDataTableProps) {
    const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(new Set());
    const getResultRowKey = (proj: DeploymentPermissionsReportProjectResult) => proj.ProjectId;
    const allProjectKeys = environment.Projects.map((proj) => getResultRowKey(proj));
    const allExpanded = allProjectKeys.every((key) => expandedProjectIds.has(key)) && allProjectKeys.length > 0;
    const handleExpandCollapseAll = () => {
        const allExpanded = allProjectKeys.every((key) => expandedProjectIds.has(key));
        if (allExpanded) {
            setExpandedProjectIds(new Set());
        }
        else {
            setExpandedProjectIds(new Set(allProjectKeys));
        }
    };
    return (<>
            <Section className={styles.padTop1}>
                <div className={styles.sectionHeaderContainer}>
                    <h2>
                        {environment.EnvironmentName} ({environment.SpaceName})
                    </h2>
                    <a role="button" href="#" className={styles.expandLink} onClick={(e) => {
            e.preventDefault();
            handleExpandCollapseAll();
        }}>
                        {allExpanded ? "COLLAPSE ALL" : "EXPAND ALL"}
                    </a>
                </div>
            </Section>
            <LegacyDataTable className={cx(styles.marginBottom3, styles.fixedTable)}>
                <LegacyDataTableHeader>
                    <LegacyDataTableRow>
                        <LegacyDataTableHeaderColumn className={styles.environmentColumn}>Environment</LegacyDataTableHeaderColumn>
                        <LegacyDataTableHeaderColumn className={styles.projectColumn}>Project</LegacyDataTableHeaderColumn>
                        <LegacyDataTableHeaderColumn className={styles.usersColumn}>Users</LegacyDataTableHeaderColumn>
                    </LegacyDataTableRow>
                </LegacyDataTableHeader>
                <LegacyDataTableBody>
                    {environment.Projects.map((project) => {
            const key = getResultRowKey(project);
            const isExpanded = expandedProjectIds.has(key);
            const hasUsers = project.Users.length > 0;
            const handleToggle = () => {
                const newSet = new Set(expandedProjectIds);
                if (isExpanded) {
                    newSet.delete(key);
                }
                else {
                    newSet.add(key);
                }
                setExpandedProjectIds(newSet);
            };
            return (<React.Fragment key={key}>
                                <ParentProjectEnvironmentRow project={project} environment={environment} isExpanded={isExpanded} hasUsers={hasUsers} onToggle={handleToggle}/>
                                {hasUsers && (<LegacyDataTableRow className={styles.collapseRow}>
                                        <LegacyDataTableRowColumn colSpan={3} className={styles.collapseCellContainer}>
                                            <Collapse in={isExpanded} timeout={200}>
                                                <table className={tableStyles}>
                                                    <tbody>
                                                        {project.Users.map((user) => (<ChildProjectEnvironmentRow key={user.Username} user={user}/>))}
                                                    </tbody>
                                                </table>
                                            </Collapse>
                                        </LegacyDataTableRowColumn>
                                    </LegacyDataTableRow>)}
                            </React.Fragment>);
        })}
                </LegacyDataTableBody>
            </LegacyDataTable>
        </>);
}
interface ParentProjectEnvironmentRowProps {
    environment: DeploymentPermissionsReportEnvironmentResult;
    project: DeploymentPermissionsReportProjectResult;
    isExpanded: boolean;
    hasUsers: boolean;
    onToggle: () => void;
}
function ParentProjectEnvironmentRow({ environment, project, isExpanded, hasUsers, onToggle }: ParentProjectEnvironmentRowProps) {
    const projectNameElement = (<InternalLink className={styles.projectLink} to={links.projectRootRedirect.generateUrl({ spaceId: environment.SpaceId, projectSlug: project.ProjectSlug })}>
            {project.ProjectName}
        </InternalLink>);
    return (<LegacyDataTableRow className={cx({ [styles.expandedRow]: isExpanded })}>
            <LegacyDataTableRowColumn className={styles.environmentColumn}>
                <EnvironmentChip environmentName={`${environment.EnvironmentName} (${environment.SpaceName})`}/>
            </LegacyDataTableRowColumn>
            <LegacyDataTableRowColumn className={styles.projectColumn}>
                <div className={styles.nameContainer}>
                    <Avatar type="project" src={client.resolve(project.ProjectLogo)} size="medium" alt={`${project.ProjectName} Logo`}/>
                    <div className={styles.name}>{projectNameElement}</div>
                </div>
            </LegacyDataTableRowColumn>
            <LegacyDataTableRowColumn className={styles.usersColumn}>
                <div className={styles.usersColumnContainer}>
                    <div>
                        <strong>{project.Users.length}</strong> {project.Users.length > 1 ? "users" : "user"} can deploy
                    </div>
                    {hasUsers && <Button importance="ghost" icon={isExpanded ? <ChevronUpIcon size={20}/> : <ChevronDownIcon size={20}/>} onClick={onToggle} size="medium"/>}
                </div>
            </LegacyDataTableRowColumn>
        </LegacyDataTableRow>);
}
interface ChildProjectEnvironmentRowProps {
    user: UserWithPermissionToDeploy;
}
function ChildProjectEnvironmentRow({ user }: ChildProjectEnvironmentRowProps) {
    const userNameElement = (<InternalLink className={styles.userLink} to={links.userPage.generateUrl({ userId: user.UserId })}>
            {user.UserDisplayName}
        </InternalLink>);
    return (<tr>
            <td className={cx(styles.noBorderTop, styles.environmentColumn)}></td>
            <td className={cx(styles.noBorderTop, styles.projectColumn)}></td>
            <td className={styles.usersColumn}>
                <div className={styles.userInfo}>
                    <strong className={styles.userName}>{userNameElement}</strong>
                    {user.Username && user.Username.trim() !== "" && <span className={styles.userEmail}>{user.Username}</span>}
                </div>
            </td>
        </tr>);
}
const styles = {
    fixedTable: css({
        tableLayout: "fixed",
        width: "100%",
    }),
    marginBottom3: css({
        marginBottom: space[64],
    }),
    padTop1: css({
        paddingTop: space[1],
    }),
    nameContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[8],
        minWidth: 0,
        width: "100%",
    }),
    name: css({
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }),
    environmentColumn: css({
        width: "30%",
        verticalAlign: "middle",
    }),
    projectColumn: css({
        width: "30%",
    }),
    usersColumn: css({
        width: "35%",
        verticalAlign: "middle",
    }),
    usersColumnContainer: css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: space[12],
    }),
    noBorderTop: css({
        borderTop: "0 !important",
    }),
    expandedRow: css({
        backgroundColor: themeTokens.color.background.secondary.default,
    }),
    collapseCellContainer: css({
        padding: "0 !important",
        borderTop: "none !important",
    }),
    collapseRow: css({
        borderTop: "none !important",
    }),
    userInfo: css({
        display: "flex",
        flexDirection: "column",
        gap: space[4],
    }),
    userName: css({
        font: text.body.bold.large,
    }),
    userEmail: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
    }),
    sectionHeaderContainer: css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    }),
    expandLink: css({
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.secondary,
        textDecoration: "none",
        "&:hover": {
            color: themeTokens.color.text.primary,
        },
    }),
    projectLink: css({
        color: themeTokens.color.text.link.default,
        textDecoration: "none",
        fontWeight: "bold",
        "&:hover": {
            color: themeTokens.color.text.link.hover,
        },
    }),
    userLink: css({
        color: themeTokens.color.text.link.default,
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    }),
    badgeContainer: css({
        lineHeight: "0",
    }),
};
